import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NzTreeNodeOptions } from 'ng-zorro-antd/tree';

import { Account, MatchExclusion } from '@libs/src/models/account.model';
import {
  Group,
  GroupCustomSegmentTypes,
  GroupRole,
} from '@libs/src/models/group.model';
import { ProgramMembership } from '@libs/src/models/program-membership.model';
import { getMembership } from '@main-client/src/app/account/account.helpers';
import { MatchSelections } from '@main-client/src/app/group-intro-selections-form/match-selections';

@Component({
  selector: 'app-group-match-exclusions-form',
  templateUrl: './group-match-exclusions-form.component.html',
})
export class GroupMatchExclusionsFormComponent implements OnInit {
  @Input() group: Group;
  @Input() account: Account;
  @Input() programMembership?: ProgramMembership;
  @Output() selectionChange = new EventEmitter<MatchSelections>();
  readonly allOptionsKey = '___ALL_OPTIONS___';
  treeSelectMap: Record<
    'role' | GroupCustomSegmentTypes['_id'],
    NzTreeNodeOptions
  > = {};
  matchSelections: MatchSelections = {
    customSegments: {},
    hiddenCustomSegments: {},
    roles: {},
  };
  formSelections: {
    customSegments: Record<GroupCustomSegmentTypes['_id'], Array<string>>;
    hiddenCustomSegments: Record<GroupCustomSegmentTypes['_id'], Array<string>>;
    roles: Array<string>;
  } = {
    customSegments: {},
    hiddenCustomSegments: {},
    roles: [],
  };

  ngOnInit() {
    this.treeSelectMap = {
      ...this.createNodeTreeFromRoles(this.group?.roles),
      ...this.createNodeTreeFromSegments(this.group?.custom_segments),
      ...this.createNodeTreeFromSegments(this.group?.hidden_custom_segments),
    };
    this.initializeFormSelections();
    this.initializeMatchSelections();
  }

  initializeFormSelections() {
    const membership = getMembership(this.account?.groups, this.group?._id);
    const matchExclusions = this.programMembership
      ? this.programMembership.match_exclusions
      : membership?.match_exclusions;
    this.formSelections = {
      customSegments: {
        ...(this.group?.custom_segments?.reduce((result, item) => {
          return {
            ...result,
            [item._id]: this.getSegmentExclusions(matchExclusions, item).map(
              ({ exclude_string }) => exclude_string,
            ),
          };
        }, {}) as Record<string, string[]>),
      },
      hiddenCustomSegments: {
        ...(this.group?.hidden_custom_segments?.reduce((result, item) => {
          return {
            ...result,
            [item._id]: this.getSegmentExclusions(matchExclusions, item).map(
              ({ exclude_string }) => exclude_string,
            ),
          };
        }, {}) as Record<string, string[]>),
      },
      roles: this.getRoleExclusions(matchExclusions).map(
        ({ exclude_string }) => exclude_string,
      ),
    };
  }

  initializeMatchSelections() {
    const membership = getMembership(this.account?.groups, this.group?._id);
    const matchExclusions = this.programMembership
      ? this.programMembership.match_exclusions
      : membership?.match_exclusions;
    this.matchSelections = {
      customSegments: this.group?.custom_segments?.reduce((result, item) => {
        const segmentExclusions = this.getSegmentExclusions(
          matchExclusions,
          item,
        );
        const excludedNodes = new Set(
          segmentExclusions.map(({ exclude_string }) => exclude_string),
        );
        return {
          ...result,
          [item._id]: item.nodes.reduce((nodesMap, node) => {
            return {
              ...nodesMap,
              [node.name]: !excludedNodes.has(node.name),
            };
          }, {}),
        };
      }, {}) as MatchSelections['customSegments'],
      hiddenCustomSegments: this.group?.hidden_custom_segments?.reduce(
        (result, item) => {
          const segmentExclusions = this.getSegmentExclusions(
            matchExclusions,
            item,
          );
          const excludedNodes = new Set(
            segmentExclusions.map(({ exclude_string }) => exclude_string),
          );
          return {
            ...result,
            [item._id]: item.nodes.reduce((nodesMap, node) => {
              return {
                ...nodesMap,
                [node.name]: !excludedNodes.has(node.name),
              };
            }, {}),
          };
        },
        {},
      ) as MatchSelections['hiddenCustomSegments'],
      roles: this.group?.roles?.reduce((result, item) => {
        const roleExclusions = this.getRoleExclusions(matchExclusions);
        const excludedRoleNames = new Set(
          roleExclusions.map(({ exclude_string }) => exclude_string),
        );
        return {
          ...result,
          [item.name]: !excludedRoleNames.has(item.name),
        };
      }, {}) as MatchSelections['roles'],
    };
    this.updateMatchSelections(this.matchSelections);
  }

  updateRoleSelection(selection: Array<string>) {
    const roleNames = this.group?.roles?.map(({ name }) => name);
    const selectionSet =
      1 === selection?.length && this.allOptionsKey === selection[0]
        ? new Set(roleNames)
        : new Set(selection);
    this.updateMatchSelections({
      ...this.matchSelections,
      roles: roleNames?.reduce((result, name) => {
        return { ...result, [name]: !selectionSet.has(name) };
      }, {}),
    });
  }

  updateSegmentSelection(
    segmentKey: keyof Pick<
      MatchSelections,
      'customSegments' | 'hiddenCustomSegments'
    >,
    segment: GroupCustomSegmentTypes,
    selection: Array<string>,
  ) {
    const nodeNames = segment.nodes.map(({ name }) => name);
    const selectionSet =
      1 === selection?.length && this.allOptionsKey === selection[0]
        ? new Set(nodeNames)
        : new Set(selection);
    this.updateMatchSelections({
      ...this.matchSelections,
      [segmentKey]: {
        ...this.matchSelections[segmentKey],
        [segment._id]: nodeNames?.reduce((result, name) => {
          return { ...result, [name]: !selectionSet.has(name) };
        }, {}),
      },
    });
  }

  updateMatchSelections(selections: MatchSelections) {
    this.matchSelections = selections;
    this.selectionChange.emit(this.matchSelections);
  }

  createNodeTreeFromRoles(
    roles: Array<GroupRole>,
  ): Record<'role', NzTreeNodeOptions> {
    return {
      role: {
        children: (roles ?? []).map(({ name }) => ({
          isLeaf: true,
          key: name,
          title: name,
          value: name,
        })),
        expanded: true,
        key: this.allOptionsKey,
        title: $localize`:@@global.allOptions:All options`,
        value: this.allOptionsKey,
      },
    } as Record<GroupCustomSegmentTypes['_id'], NzTreeNodeOptions>;
  }

  createNodeTreeFromSegments(
    segments: Array<GroupCustomSegmentTypes>,
  ): Record<GroupCustomSegmentTypes['_id'], NzTreeNodeOptions> {
    return (segments ?? []).reduce((result, item) => {
      return {
        ...result,
        [item._id]: {
          children: item.nodes.map(({ name }) => ({
            isLeaf: true,
            key: name,
            title: name,
            value: name,
          })),
          expanded: true,
          key: this.allOptionsKey,
          title: $localize`:@@global.allOptions:All options`,
          value: this.allOptionsKey,
        } satisfies NzTreeNodeOptions,
      };
    }, {}) as Record<GroupCustomSegmentTypes['_id'], NzTreeNodeOptions>;
  }

  getSegmentExclusions(
    exclusions: Array<MatchExclusion>,
    segment: GroupCustomSegmentTypes,
  ): Array<MatchExclusion> {
    return (exclusions ?? []).filter(
      ({ exclusion_type, exclude_id }) =>
        'custom_segment' === exclusion_type && segment?._id === exclude_id,
    );
  }

  getRoleExclusions(exclusions: Array<MatchExclusion>): Array<MatchExclusion> {
    return (exclusions ?? []).filter(
      ({ exclusion_type }) => 'match_segment' === exclusion_type,
    );
  }
}
